import { clamp } from '../core/math';
import { SOIL_KEYS } from '../data/types';
import type { SoilKey } from '../data/types';
import type { Plan } from '../model';

/** 画面で変えられる施工計画の数値項目（土質を除く）。 */
export type PlanNumberKey = Exclude<keyof Plan, 'soil'>;
/** 現場の地形を変える項目。 */
export type GradeKey = 'FH' | 'pit';

export interface FieldRange {
  label: string;
  unit: string;
  min: number;
  max: number;
  step: number;
}

/**
 * 入力値の範囲（元仕様 §6.13、サンプルの `FIELDS`）。
 * 範囲の外の値は範囲に収める。刻みへの丸めはしない（サンプルと同じ）。
 */
export const PLAN_FIELDS: Readonly<Record<PlanNumberKey, FieldRange>> = {
  usable: { label: '流用可能率', unit: '%', min: 0, max: 100, step: 5 },
  cap: { label: 'ダンプ積載（ほぐし）', unit: 'm³/台', min: 2, max: 12, step: 0.5 },
  ex: { label: 'バックホウ（0.7m³級）', unit: '台', min: 0, max: 12, step: 1 },
  qEx: { label: '日当り掘削積込', unit: 'm³/台日', min: 100, max: 800, step: 10 },
  trucks: { label: 'ダンプトラック（10t）', unit: '台', min: 0, max: 60, step: 1 },
  dExt: { label: '場外運搬距離', unit: 'km', min: 1, max: 60, step: 1 },
  rollers: { label: 'ブルドーザ＋ローラ', unit: '組', min: 0, max: 8, step: 1 },
  qR: { label: '日当り締固め', unit: 'm³/組日', min: 200, max: 1500, step: 50 },
  hours: { label: '稼働時間', unit: 'h/日', min: 4, max: 12, step: 1 },
  days: { label: '稼働日', unit: '日/週', min: 4, max: 7, step: 1 },
  rain: { label: '雨天・休止率', unit: '%', min: 0, max: 40, step: 1 },
  winterF: { label: '冬期の土工稼働率', unit: '%', min: 0, max: 100, step: 5 },
};

export const GRADE_FIELDS: Readonly<Record<GradeKey, FieldRange>> = {
  FH: { label: '計画地盤高 FH', unit: 'T.P. m', min: -50, max: 3000, step: 0.1 },
  pit: { label: '根切り深さ', unit: 'm', min: 0, max: 20, step: 0.1 },
};

/** 土量算出の欄（土質を除く）と、土工計画の欄。並びは元仕様 §3.9 のタブと同じ。 */
export const EARTH_PLAN_KEYS: readonly PlanNumberKey[] = ['usable', 'cap'];
export const WORK_PLAN_KEYS: readonly PlanNumberKey[] = [
  'ex',
  'qEx',
  'trucks',
  'dExt',
  'rollers',
  'qR',
  'hours',
  'days',
  'rain',
  'winterF',
];

export const isPlanNumberKey = (k: string): k is PlanNumberKey => Object.hasOwn(PLAN_FIELDS, k);
export const isGradeKey = (k: string): k is GradeKey => Object.hasOwn(GRADE_FIELDS, k);
export const isSoilKey = (k: string): k is SoilKey => (SOIL_KEYS as readonly string[]).includes(k);

/**
 * 入力の文字や数を、範囲に収めた数にする。数でなければ null（入力を捨てる。NFR-SEC-04）。
 * 空欄も数でないものとして扱う。
 */
export function normalizeInput(range: FieldRange, raw: string | number): number | null {
  if (typeof raw === 'string' && raw.trim() === '') return null;
  const v = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(v)) return null;
  return clamp(v, range.min, range.max);
}
